import { EligibleRegisterGroup, RegisterAgeGroupEntry, RegisterComposition } from '@schooly/api';

import {
  AgeGroupEntry,
  isSessionScope,
  RegisterGroupTarget,
  RegisterPeriod,
  RegisterPeriodGroupIds,
  RegisterPeriodStructure,
  RegisterScope,
  RegisterSession,
  RosterGroup,
} from '../types';

const distinctByAgeGroup = <T extends { age_group: { id: string } }>(entries: T[]): T[] => [
  ...new Map(entries.map((entry) => [entry.age_group.id, entry])).values(),
];

const sumStudents = (entries: AgeGroupEntry[]): number =>
  entries.reduce((total, entry) => total + entry.student_count, 0);

const getScopeEntries = (group: RosterGroup, scope: RegisterScope): AgeGroupEntry[] => {
  const { snapshot } = group;
  if (!snapshot) return group.age_group_entries;

  const inSnapshot = isSessionScope(scope)
    ? snapshot.sessions.includes(scope.session)
    : snapshot.period_ids.includes(scope.periodId);

  return inSnapshot ? snapshot.age_group_entries : group.age_group_entries;
};

const matchesScope = (entry: AgeGroupEntry, scope: RegisterScope) => {
  if (isSessionScope(scope)) return entry.session === scope.session;
  if ('period_id' in entry) return entry.period_id === scope.periodId;

  return entry.per_period_enabled;
};

const getTrackedEntries = (group: RosterGroup, scope: RegisterScope): AgeGroupEntry[] =>
  distinctByAgeGroup(getScopeEntries(group, scope).filter((entry) => matchesScope(entry, scope)));

const getTrackedStudentCount = (group: RosterGroup, scope: RegisterScope): number =>
  sumStudents(getTrackedEntries(group, scope));

const getUntrackedAgeGroups = (group: RosterGroup, scope: RegisterScope): AgeGroupEntry[] => {
  const trackedIds = new Set(getTrackedEntries(group, scope).map((entry) => entry.age_group.id));

  return distinctByAgeGroup(getScopeEntries(group, scope)).filter(
    (entry) => !trackedIds.has(entry.age_group.id),
  );
};

type GroupScopeCounts = {
  trackedCount: number;
  untrackedCount: number;
  untrackedAgeGroups: AgeGroupEntry[];
  totalCount: number;
};

export const getGroupScopeCounts = (group: RosterGroup, scope: RegisterScope): GroupScopeCounts => {
  const untrackedAgeGroups = getUntrackedAgeGroups(group, scope);
  const trackedCount = getTrackedStudentCount(group, scope);
  const untrackedCount = sumStudents(untrackedAgeGroups);

  return {
    trackedCount,
    untrackedCount,
    untrackedAgeGroups,
    totalCount: trackedCount + untrackedCount,
  };
};

export const getTotalStudentCount = (group: EligibleRegisterGroup): number =>
  sumStudents(distinctByAgeGroup(group.age_group_entries));

export const getStudentCountForSession = (
  groups: RosterGroup[],
  session: RegisterSession,
): number => groups.reduce((total, group) => total + getTrackedStudentCount(group, { session }), 0);

export const getFilledStructureCount = (
  structures: RegisterPeriodStructure[],
  periodsByStructureId: Record<string, RegisterPeriod[]>,
  periodGroupIds: RegisterPeriodGroupIds,
): number =>
  structures.filter((structure) =>
    (periodsByStructureId[structure.id] ?? []).some(
      (period) => (periodGroupIds[period.id] ?? []).length > 0,
    ),
  ).length;

export type StructurePeriodCoverage = {
  structure: RegisterPeriodStructure;
  periods: {
    id: string;
    order: number;
    register_session: RegisterPeriod['register_session'];
    groupCount: number;
  }[];
};

export const getPerPeriodStructureCoverage = (
  structures: RegisterPeriodStructure[],
  periodsByStructureId: Record<string, RegisterPeriod[]>,
  periodGroupIds: RegisterPeriodGroupIds,
): StructurePeriodCoverage[] =>
  structures.map((structure) => ({
    structure,
    periods: (periodsByStructureId[structure.id] ?? []).map((period) => ({
      id: period.id,
      order: period.order,
      register_session: period.register_session,
      groupCount: (periodGroupIds[period.id] ?? []).length,
    })),
  }));

type GetGroupEntryCountParams = {
  composition: RegisterComposition;
  groupId: string;
  target: RegisterGroupTarget;
};

const matchesTarget = (entry: RegisterAgeGroupEntry, target: RegisterGroupTarget) =>
  (!!target.session && entry.session === target.session) ||
  (!!target.periodId && entry.period_id === target.periodId);

export const getGroupEntryCount = ({
  composition,
  groupId,
  target,
}: GetGroupEntryCountParams): number => {
  const group = composition.groups.find((item) => item.group_id === groupId);
  if (!group) return 0;

  return group.age_group_entries
    .filter((entry) => matchesTarget(entry, target))
    .reduce((total, entry) => total + entry.attendance_record_count, 0);
};

export const getRegisterEntriesCount = (composition: RegisterComposition): number =>
  composition.groups.reduce(
    (total, group) =>
      total +
      group.age_group_entries.reduce(
        (entryTotal, entry) => entryTotal + entry.attendance_record_count,
        0,
      ),
    0,
  );
